import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility as VisibilityIcon, Add as AddIcon, Email as EmailIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { normalizePricingRow } from '../../utils/pricing';

const ManagerCustomers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [apiError, setApiError] = useState('');
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesMessage, setNotesMessage] = useState('');
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteData, setQuoteData] = useState({
    diesel_quantity: '',
    def_quantity: '',
    frequency: 'one-time',
    delivery_fee: '',
    delivery_address: '',
    expires_at: '',
  });
  const [quoteMessage, setQuoteMessage] = useState('');
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [currentPricing, setCurrentPricing] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [createCustomerError, setCreateCustomerError] = useState('');
  const [createCustomerSuccess, setCreateCustomerSuccess] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [customerFormData, setCustomerFormData] = useState({
    email: '',
    company_name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setApiError('');
      const response = await api.get('/managers/customers/');
      console.log('Customers API response:', response);
      
      // Handle paginated response
      let customersData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          customersData = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          customersData = response.data.results;
        } else if (typeof response.data === 'object') {
          customersData = response.data.data || response.data.customers || [];
        }
      }
      
      console.log('Processed customers data:', customersData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Error fetching customers:', error);
      console.error('Error response:', error.response);
      setCustomers([]);
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.error || 
                      error.message ||
                      'Failed to load customers';
      setApiError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = async (customer) => {
    setSelectedCustomer(customer);
    setProfileDialogOpen(true);
    setProfileLoading(true);
    setCustomerData(null);
    setTabValue(0);
    setNotes('');
    setNotesMessage('');
    setReminderMessage('');
    
    try {
      const response = await api.get(`/managers/customer-detail/?customer_id=${customer.id}`);
      setCustomerData(response.data);
      setNotes(response.data.profile?.notes || '');
      setApiError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error fetching customer details:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'Failed to load customer details';
      setApiError(errorMsg);
      setCustomerData(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedCustomer?.id) return;
    
    setSavingNotes(true);
    setNotesMessage('');
    
    try {
      await api.put('/managers/customer-notes/', {
        customer_id: selectedCustomer.id,
        notes: notes,
      });
      setNotesMessage('Notes saved successfully');
      // Update the customer data
      if (customerData) {
        setCustomerData({
          ...customerData,
          profile: {
            ...customerData.profile,
            notes: notes,
          },
        });
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      setNotesMessage('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCloseProfileDialog = () => {
    setProfileDialogOpen(false);
    setSelectedCustomer(null);
    setCustomerData(null);
    setTabValue(0);
    setReminderMessage('');
  };

  const handleSendPaymentReminders = async () => {
    if (!selectedCustomer?.id) return;
    setSendingReminders(true);
    setReminderMessage('');
    try {
      const res = await api.post('/managers/send-invoice-payment-reminders/', {
        customer_id: selectedCustomer.id,
      });
      setReminderMessage(
        res.data?.message || `Sent ${res.data?.sent ?? 0} payment reminder(s).`,
      );
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to send payment reminders';
      setReminderMessage(`Failed to send reminders: ${msg}`);
    } finally {
      setSendingReminders(false);
    }
  };

  const handleOpenQuoteDialog = async () => {
    if (selectedCustomer && customerData?.profile) {
      setQuoteData({
        diesel_quantity: '',
        def_quantity: '',
        frequency: 'one-time',
        delivery_fee: '',
        delivery_address: customerData.profile.address || '',
        expires_at: '',
      });
      setQuoteMessage('');
      setCalculatedPrice(0);
      setQuoteDialogOpen(true);
      
      // Fetch current pricing
      try {
        const response = await api.get('/pricing/pricing/current/');
        setCurrentPricing(normalizePricingRow(response.data));
      } catch (error) {
        console.error('Error fetching current pricing:', error);
        setCurrentPricing(null);
      }
    }
  };

  const handleCloseQuoteDialog = () => {
    setQuoteDialogOpen(false);
    setQuoteData({
      diesel_quantity: '',
      def_quantity: '',
      frequency: 'one-time',
      delivery_fee: '',
      delivery_address: '',
      expires_at: '',
    });
    setQuoteMessage('');
    setCalculatedPrice(0);
    setCurrentPricing(null);
  };

  const handleCreateQuote = async () => {
    if (!selectedCustomer?.id) return;
    
    setCreatingQuote(true);
    setQuoteMessage('');
    
    try {
      const response = await api.post('/managers/create-quote/', {
        customer: selectedCustomer.id,
        diesel_quantity: parseFloat(quoteData.diesel_quantity) || 0,
        def_quantity: parseFloat(quoteData.def_quantity) || 0,
        frequency: quoteData.frequency,
        delivery_fee: parseFloat(quoteData.delivery_fee) || 0,
        delivery_address: quoteData.delivery_address,
        expires_at: quoteData.expires_at || null,
      });
      
      setQuoteMessage('Quote created successfully!');
      setTimeout(() => {
        handleCloseQuoteDialog();
        setQuoteMessage('');
      }, 1500);
    } catch (error) {
      console.error('Error creating quote:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Failed to create quote. Please check your input.';
      setQuoteMessage(errorMsg);
    } finally {
      setCreatingQuote(false);
    }
  };

  // Calculate estimated price when quantities or delivery fee change (including taxes)
  useEffect(() => {
    if (currentPricing) {
      const dieselQty = parseFloat(quoteData.diesel_quantity) || 0;
      const defQty = parseFloat(quoteData.def_quantity) || 0;
      const deliveryFee = parseFloat(quoteData.delivery_fee) || 0;
      
      const dieselPrice = parseFloat(currentPricing.diesel_price_per_liter) || 0;
      const defPrice = parseFloat(currentPricing.def_price_per_liter) || 0;
      const dieselTax = parseFloat(currentPricing.diesel_sales_tax) || 0;
      const defTax = parseFloat(currentPricing.def_sales_tax) || 0;
      
      // Calculate fuel costs (before tax)
      const dieselCost = dieselQty * dieselPrice;
      const defCost = defQty * defPrice;
      
      // Calculate tax amounts
      const dieselTaxAmount = dieselCost * dieselTax;
      const defTaxAmount = defCost * defTax;
      
      // Total = fuel costs + taxes + delivery fee
      const calculated = dieselCost + dieselTaxAmount + defCost + defTaxAmount + deliveryFee;
      setCalculatedPrice(calculated);
    } else {
      setCalculatedPrice(0);
    }
  }, [quoteData.diesel_quantity, quoteData.def_quantity, quoteData.delivery_fee, currentPricing]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleCreateCustomer = async () => {
    setCreatingCustomer(true);
    setCreateCustomerError('');
    setCreateCustomerSuccess('');

    // Validate required fields
    if (!customerFormData.email || !customerFormData.company_name || !customerFormData.phone_number || 
        !customerFormData.address || !customerFormData.city || !customerFormData.state || !customerFormData.zip_code) {
      setCreateCustomerError('Please fill in all required fields');
      setCreatingCustomer(false);
      return;
    }

    try {
      const response = await api.post('/managers/create-customer/', customerFormData);
      setCreateCustomerSuccess(
        response.data.email_sent 
          ? 'Customer created successfully! Invitation email has been sent.'
          : 'Customer created successfully, but email could not be sent. Please contact the customer manually.'
      );
      
      // Refresh customers list
      await fetchCustomers();
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        setCreateCustomerDialogOpen(false);
        setCreateCustomerSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error creating customer:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.response?.data?.email?.[0] ||
                      'Failed to create customer. Please try again.';
      setCreateCustomerError(errorMsg);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Customers</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateCustomerDialogOpen(true);
              setCustomerFormData({
                email: '',
                company_name: '',
                phone_number: '',
                address: '',
                city: '',
                state: '',
                zip_code: '',
                notes: '',
              });
              setCreateCustomerError('');
              setCreateCustomerSuccess('');
            }}
          >
            Add New Customer
          </Button>
        </Box>
        
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!Array.isArray(customers) || customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No customers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow 
                    key={customer?.id || Math.random()}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewProfile(customer)}
                  >
                    <TableCell>{customer?.company_name || 'N/A'}</TableCell>
                    <TableCell>{customer?.email || customer?.user?.email || 'N/A'}</TableCell>
                    <TableCell>{customer?.phone_number || 'N/A'}</TableCell>
                    <TableCell>
                      {customer?.address ? (
                        customer.address.length > 50 
                          ? `${customer.address.substring(0, 50)}...` 
                          : customer.address
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(customer?.created_at)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProfile(customer);
                        }}
                      >
                        View Profile
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Customer Profile Dialog - Shows profile as customer sees it */}
        <Dialog
          open={profileDialogOpen}
          onClose={handleCloseProfileDialog}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: { height: { xs: '100vh', sm: '90vh' } }
          }}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h5">
              Customer Profile
              {customerData?.profile?.company_name && ` - ${customerData.profile.company_name}`}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {profileLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : customerData ? (
              <Box>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                  <Tab label="Profile" />
                  <Tab label="Orders" />
                  <Tab label="Invoices" />
                  <Tab label="Quotes" />
                </Tabs>

                {/* Profile Tab */}
                {tabValue === 0 && (
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Profile Information
                    </Typography>
                    {customerData.profile && (
                      <>
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                          <Typography variant="h6" gutterBottom>Current Profile Information</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">Email</Typography>
                              <Typography variant="body1">
                                {customerData.profile.email || customerData.profile.user?.email || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">Company Name</Typography>
                              <Typography variant="body1">
                                {customerData.profile.company_name || 'Not set'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                              <Typography variant="body1">
                                {customerData.profile.phone_number || 'Not set'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary">Address</Typography>
                              <Typography variant="body1">
                                {[
                                  customerData.profile.address,
                                  customerData.profile.city,
                                  customerData.profile.state,
                                  customerData.profile.zip_code
                                ].filter(Boolean).join(', ') || 'Not set'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">Registered Date</Typography>
                              <Typography variant="body1">
                                {formatDate(customerData.profile.created_at)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                              <Typography variant="body1">
                                {formatDate(customerData.profile.updated_at)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                          Profile Details (Read-Only)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Company Name"
                              value={customerData.profile.company_name || ''}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              value={customerData.profile.phone_number || ''}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Complete Address"
                              value={customerData.profile.address || ''}
                              multiline
                              rows={2}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="City"
                              value={customerData.profile.city || ''}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="State"
                              value={customerData.profile.state || ''}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Zip Code"
                              value={customerData.profile.zip_code || ''}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                        </Grid>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                          Notes & Comments (Manager Only)
                        </Typography>
                        {notesMessage && (
                          <Alert 
                            severity={notesMessage.includes('success') ? 'success' : 'error'} 
                            sx={{ mb: 2 }}
                            onClose={() => setNotesMessage('')}
                          >
                            {notesMessage}
                          </Alert>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Notes/Comments"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              multiline
                              rows={6}
                              placeholder="Add internal notes and comments about this customer..."
                              helperText="These notes are only visible to managers"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              onClick={handleSaveNotes}
                              disabled={savingNotes}
                            >
                              {savingNotes ? 'Saving...' : 'Save Notes'}
                            </Button>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Paper>
                )}

                {/* Orders Tab */}
                {tabValue === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Orders ({customerData.jobs?.length || 0})
                    </Typography>
                    {customerData.jobs && customerData.jobs.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Order ID</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell>Quantity</TableCell>
                              <TableCell>Frequency</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Payment Status</TableCell>
                              <TableCell>Scheduled Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.jobs.map((job) => (
                              <TableRow key={job.id}>
                                <TableCell>{job.id?.substring(0, 8) || 'N/A'}</TableCell>
                                <TableCell>{job.job_location || 'N/A'}</TableCell>
                                <TableCell>
                                  {job.diesel_quantity > 0 && `${job.diesel_quantity} gal Diesel`}
                                  {job.diesel_quantity > 0 && job.def_quantity > 0 && ' / '}
                                  {job.def_quantity > 0 && `${job.def_quantity} gal DEF`}
                                  {(!job.diesel_quantity || job.diesel_quantity === 0) && (!job.def_quantity || job.def_quantity === 0) && 'N/A'}
                                </TableCell>
                                <TableCell>{job.frequency || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={job.job_status || 'N/A'}
                                    color={
                                      job.job_status === 'complete' ? 'success' :
                                      job.job_status === 'active' ? 'primary' :
                                      job.job_status === 'pending' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={job.payment_status || 'N/A'}
                                    color={job.payment_status === 'paid' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{formatDate(job.scheduled_date)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No orders found for this customer.</Alert>
                    )}
                  </Box>
                )}

                {/* Invoices Tab */}
                {tabValue === 2 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">
                        Invoices ({customerData.invoices?.length || 0})
                      </Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={sendingReminders ? <CircularProgress size={18} /> : <EmailIcon />}
                        disabled={sendingReminders || !customerData.invoices?.length}
                        onClick={handleSendPaymentReminders}
                      >
                        Send payment reminders
                      </Button>
                    </Box>
                    {reminderMessage && (
                      <Alert
                        severity={
                          reminderMessage.startsWith('Failed to send')
                            ? 'error'
                            : /^Sent 0 payment reminder/.test(reminderMessage)
                              ? 'info'
                              : 'success'
                        }
                        sx={{ mb: 2 }}
                        onClose={() => setReminderMessage('')}
                      >
                        {reminderMessage}
                      </Alert>
                    )}
                    {customerData.invoices && customerData.invoices.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Invoice Number</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Created Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.invoices.map((invoice) => (
                              <TableRow key={invoice.id}>
                                <TableCell>{invoice.invoice_number || 'N/A'}</TableCell>
                                <TableCell>
                                  ${invoice.total_amount ? parseFloat(invoice.total_amount).toFixed(2) : '0.00'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={invoice.status || 'N/A'}
                                    color={
                                      invoice.status === 'paid' ? 'success' :
                                      invoice.status === 'submitted' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{formatDate(invoice.due_date)}</TableCell>
                                <TableCell>{formatDate(invoice.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No invoices found for this customer.</Alert>
                    )}
                  </Box>
                )}

                {/* Quotes Tab */}
                {tabValue === 3 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Quotes ({customerData.quotes?.length || 0})
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenQuoteDialog}
                      >
                        Create Quote
                      </Button>
                    </Box>
                    {customerData.quotes && customerData.quotes.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Quote ID</TableCell>
                              <TableCell>Diesel Quantity</TableCell>
                              <TableCell>DEF Quantity</TableCell>
                              <TableCell>Frequency</TableCell>
                              <TableCell>Estimated Price</TableCell>
                              <TableCell>Delivery Fee</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Expires At</TableCell>
                              <TableCell>Created Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.quotes.map((quote) => (
                              <TableRow key={quote.id}>
                                <TableCell>{quote.id?.substring(0, 8) || 'N/A'}</TableCell>
                                <TableCell>{parseFloat(quote.diesel_quantity || 0).toFixed(2)} gal</TableCell>
                                <TableCell>{parseFloat(quote.def_quantity || 0).toFixed(2)} gal</TableCell>
                                <TableCell>{quote.frequency || 'N/A'}</TableCell>
                                <TableCell>${parseFloat(quote.estimated_price || 0).toFixed(2)}</TableCell>
                                <TableCell>${parseFloat(quote.delivery_fee || 0).toFixed(2)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={quote.status || 'N/A'}
                                    color={
                                      quote.status === 'accepted' ? 'success' :
                                      quote.status === 'submitted' ? 'info' :
                                      quote.status === 'rejected' ? 'error' :
                                      quote.status === 'expired' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{quote.expires_at ? formatDate(quote.expires_at) : 'N/A'}</TableCell>
                                <TableCell>{formatDate(quote.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No quotes found for this customer.</Alert>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="error">Failed to load customer data</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProfileDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Create Quote Dialog */}
        <Dialog
          open={quoteDialogOpen}
          onClose={handleCloseQuoteDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Create Quote for {selectedCustomer?.company_name || 'Customer'}</DialogTitle>
          <DialogContent>
            {quoteMessage && (
              <Alert 
                severity={quoteMessage.includes('success') ? 'success' : 'error'} 
                sx={{ mb: 2 }}
                onClose={() => setQuoteMessage('')}
              >
                {quoteMessage}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Diesel quantity (liters)"
                  type="number"
                  value={quoteData.diesel_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, diesel_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DEF quantity (liters)"
                  type="number"
                  value={quoteData.def_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, def_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={quoteData.frequency}
                    onChange={(e) => setQuoteData({ ...quoteData, frequency: e.target.value })}
                    label="Frequency"
                  >
                    <MenuItem value="one-time">One-time</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {currentPricing && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Current Pricing
                    </Typography>
                    <Typography variant="body2">
                      Diesel: ${currentPricing.diesel_price_per_liter}/L 
                      {currentPricing.diesel_sales_tax > 0 && ` (${(parseFloat(currentPricing.diesel_sales_tax) * 100).toFixed(2)}% tax)`}
                      {' | '}
                      DEF: ${currentPricing.def_price_per_liter}/L
                      {currentPricing.def_sales_tax > 0 && ` (${(parseFloat(currentPricing.def_sales_tax) * 100).toFixed(2)}% tax)`}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Delivery Fee ($)"
                  type="number"
                  value={quoteData.delivery_fee}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_fee: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estimated Price
                  </Typography>
                  <Typography variant="h6">
                    ${calculatedPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    (Calculated from current prices)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Delivery Address"
                  multiline
                  rows={3}
                  value={quoteData.delivery_address}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expires At (Optional)"
                  type="date"
                  value={quoteData.expires_at}
                  onChange={(e) => setQuoteData({ ...quoteData, expires_at: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseQuoteDialog} disabled={creatingQuote}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateQuote}
              variant="contained"
              disabled={creatingQuote || (!quoteData.diesel_quantity && !quoteData.def_quantity) || !quoteData.delivery_address || !currentPricing}
            >
              {creatingQuote ? 'Creating...' : 'Create Quote'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Customer Dialog */}
        <Dialog
          open={createCustomerDialogOpen}
          onClose={() => !creatingCustomer && setCreateCustomerDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogContent dividers>
            {createCustomerError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateCustomerError('')}>
                {createCustomerError}
              </Alert>
            )}
            {createCustomerSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateCustomerSuccess('')}>
                {createCustomerSuccess}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email *"
                  type="email"
                  value={customerFormData.email}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                  helperText="Customer will receive an invitation email to create their account"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="company_name"
                  label="Company Name *"
                  value={customerFormData.company_name}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="phone_number"
                  label="Phone Number *"
                  value={customerFormData.phone_number}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Street Address *"
                  value={customerFormData.address}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="city"
                  label="City *"
                  value={customerFormData.city}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="state"
                  label="State *"
                  value={customerFormData.state}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  helperText="2-letter code (e.g., CA)"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="zip_code"
                  label="ZIP Code *"
                  value={customerFormData.zip_code}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes (Optional)"
                  value={customerFormData.notes}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Internal notes about this customer"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCreateCustomerDialogOpen(false)} 
              disabled={creatingCustomer}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustomer}
              variant="contained"
              color="primary"
              disabled={creatingCustomer}
            >
              {creatingCustomer ? 'Creating...' : 'Create Customer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerCustomers;

